import { CreateContextStateType, RaffleDrawProviderProps, UpdateContextValueType } from '@src/lib/types/create-raffle-draw';
import { useInputHelper } from '@src/utils/inputs-helper';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { createContext, useContext } from 'react';
import { useService } from '../hooks';
import { useRaffleDrawContext } from '../provider';

const initialState: CreateContextStateType = {
  "code": "",
  "description": "",
  "name": "",
  "numWinners": "",
  "drawAt": null,
  "startAt": null,
  drawTime: null,
  "thumbnail": "",
  "ticketLimit": "",
  "ticketPrice": "",
  status: "INACTIVE",
  subject: "REGULAR",
} as CreateContextStateType

export const UpdateContext = createContext<UpdateContextValueType>({} as UpdateContextValueType);

const CreateProvider = ({ children }: RaffleDrawProviderProps) => {
  const { updateDetails, onRefresh } = useService();
  const [ error, setError ] = React.useState({});
  const { openEdit, state: STATE } = useRaffleDrawContext();
  const { state, onSetInitial, onDispatch } = useInputHelper(initialState);

  const onUpdating = React.useCallback(() => {
    const Err: any = {};
    if(!state.description){
      Err.description = true
    }
    if(!state.thumbnail){
      Err.thumbnail = true
    }
    if(!state.startAt){
      Err.startAt = true
    }
    if(!state.ticketPrice){
      Err.ticketPrice = true
    }
    if(!state.drawAt){
      Err.drawAt = true
    }
    if(!state.ticketLimit){
      Err.ticketLimit = true
    }
    if(!state.drawTime){
      Err.drawTime = true
    }
    if(!state.numWinners){
      Err.numWinners = true
    }

    setError(Err);

    if(_.isEmpty(Err)){
      const params: any = {
        id: state.id,
        code: state.code,
        description: state.description,
        thumbnail: state.thumbnail,
        ticketPrice: state.ticketPrice,
        ticketLimit: state.ticketLimit,
        numWinners: state.numWinners,
        status: state.status,
        subject: state.subject,
        startDate: state.startAt.format('YYYY-MM-DD'),
        drawDate: state.drawAt.format('YYYY-MM-DD'),
        drawTime: state.drawTime.format('HH:mm')
      };

      updateDetails.mutate(params,
        {
          onSuccess: () => {
            openEdit(null);
            onRefresh();
            onSetInitial(initialState);
          }
        }
      );
    }
  }, [state, updateDetails, openEdit, onRefresh, onSetInitial]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    state,
    error,
    onDispatch,
    isLoading: updateDetails.isPending,
    onUpdating,
  };

  React.useEffect(() => {
    if(!_.isEmpty(error)){
      setError({});
    }
  },[state]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if(!_.isEmpty(STATE.editDetails)){
      onSetInitial({
        ...STATE.editDetails,
        startAt: STATE.editDetails.startDate ? dayjs(STATE.editDetails.startDate) : null,
        drawAt: STATE.editDetails.drawDate ? dayjs(STATE.editDetails.drawDate) : null,
        drawTime: STATE.editDetails.drawTime ? dayjs(`1970-01-01T${STATE.editDetails.drawTime}`) : null,
      });
    }
  },[STATE.editDetails, onSetInitial]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UpdateContext.Provider value={value}>
      {children}
    </UpdateContext.Provider>
  );
};

export const useUpdateContext = () => {
  return useContext(UpdateContext);
}

export default CreateProvider;
